import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from './supabase';

/**
 * ESG reference data (tracker 44): the carbon conversion factors and the
 * refrigerant global warming potentials every workspace's ESG figures use.
 * Seeded with the UK government 2024 factors and AR4 GWPs; a new year's
 * factors are added here and the nightly ESG rollup picks them up (the newest
 * year at or before the month is used). Nothing about them is in the app's code.
 */

type Factor = {
  id: string;
  factor_year: number;
  region: string;
  fuel: string;
  unit: string;
  kg_co2e_per_unit: number;
  scope: number;
  source: string | null;
  is_estimate: boolean;
};
type Gwp = { refrigerant: string; gwp: number; is_fgas: boolean; note: string | null };

const FUELS = ['electricity', 'gas', 'oil', 'heat', 'water'];

export default function EsgFactors({ toast }: { toast: (msg: string) => void }) {
  const [factors, setFactors] = useState<Factor[] | null>(null);
  const [gwps, setGwps] = useState<Gwp[] | null>(null);
  const [draft, setDraft] = useState({ year: String(new Date().getFullYear()), fuel: 'electricity', unit: 'kWh', kg: '', scope: '2', source: '' });
  const [gwpDraft, setGwpDraft] = useState({ refrigerant: '', gwp: '', isFgas: true });

  const load = useCallback(async () => {
    const [f, g] = await Promise.all([
      supabase.from('carbon_conversion_factors').select('*').order('factor_year', { ascending: false }).order('fuel'),
      supabase.from('refrigerant_gwp').select('*').order('refrigerant'),
    ]);
    if (f.error) toast(`Factors: ${f.error.message}`);
    if (g.error) toast(`GWP: ${g.error.message}`);
    setFactors((f.data ?? []) as Factor[]);
    setGwps((g.data ?? []) as Gwp[]);
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveFactor = async (row: { year: number; fuel: string; unit: string; kg: number; scope: number; source: string | null; region?: string }) => {
    const { error } = await supabase.rpc('admin_set_carbon_factor', {
      p_year: row.year,
      p_region: row.region ?? 'UK',
      p_fuel: row.fuel,
      p_unit: row.unit,
      p_kg: row.kg,
      p_scope: row.scope,
      p_source: row.source,
      p_is_estimate: true,
    });
    toast(error ? `Refused: ${error.message}` : `Saved ${row.year} ${row.fuel}`);
    await load();
  };

  const addFactor = () => {
    const year = Number(draft.year);
    const kg = Number(draft.kg);
    if (!Number.isInteger(year) || !Number.isFinite(kg) || kg < 0 || draft.kg.trim() === '') return toast('Enter a year and kg CO2e per unit');
    void saveFactor({ year, fuel: draft.fuel, unit: draft.unit, kg, scope: Number(draft.scope), source: draft.source.trim() || null });
  };

  const removeFactor = async (f: Factor) => {
    if (!window.confirm(`Remove the ${f.factor_year} ${f.fuel} factor? Months that used it fall back to the nearest earlier year at the next rollup.`)) return;
    const { error } = await supabase.rpc('admin_remove_carbon_factor', { p_id: f.id });
    toast(error ? `Refused: ${error.message}` : 'Removed');
    await load();
  };

  const saveGwp = async (refrigerant: string, gwp: number, isFgas: boolean) => {
    const { error } = await supabase.rpc('admin_set_refrigerant_gwp', { p_refrigerant: refrigerant, p_gwp: gwp, p_is_fgas: isFgas, p_note: null });
    toast(error ? `Refused: ${error.message}` : `Saved ${refrigerant.toUpperCase()}`);
    await load();
  };

  return (
    <div>
      <div className="card">
        <div style={{ fontWeight: 700, marginBottom: 2 }}>Carbon conversion factors</div>
        <p className="sub" style={{ marginBottom: 12 }}>
          kg CO2e per unit, by year. Scope 1 is fuel burnt on site; scope 2 is electricity bought. Each month uses the newest year at or
          before it. The ESG tab labels carbon as an estimate.
        </p>
        {factors == null ? <p className="muted">Loading…</p> : null}
        <table className="matrix" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Year</th>
              <th>Region</th>
              <th>Fuel</th>
              <th>Unit</th>
              <th>kg CO2e / unit</th>
              <th>Scope</th>
              <th>Source</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(factors ?? []).map((f) => (
              <tr key={f.id}>
                <td>{f.factor_year}</td>
                <td>{f.region}</td>
                <td>{f.fuel}</td>
                <td>{f.unit}</td>
                <td>
                  <input
                    defaultValue={String(f.kg_co2e_per_unit)}
                    style={{ width: 90 }}
                    onBlur={(e) => {
                      const kg = Number(e.target.value);
                      if (Number.isFinite(kg) && kg >= 0 && kg !== Number(f.kg_co2e_per_unit)) {
                        void saveFactor({ year: f.factor_year, region: f.region, fuel: f.fuel, unit: f.unit, kg, scope: f.scope, source: f.source });
                      }
                    }}
                  />
                </td>
                <td>{f.scope}</td>
                <td className="muted" style={{ fontSize: 12 }}>{f.source ?? ''}</td>
                <td>
                  <button className="link" onClick={() => void removeFactor(f)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <strong style={{ fontSize: 13 }}>Add or replace</strong>
          <input value={draft.year} onChange={(e) => setDraft({ ...draft, year: e.target.value })} style={{ width: 70 }} aria-label="Year" />
          <select
            value={draft.fuel}
            onChange={(e) => setDraft({ ...draft, fuel: e.target.value, unit: e.target.value === 'water' ? 'm3' : 'kWh', scope: e.target.value === 'electricity' ? '2' : '1' })}
            aria-label="Fuel"
          >
            {FUELS.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>
          <span className="muted">{draft.unit}</span>
          <input value={draft.kg} onChange={(e) => setDraft({ ...draft, kg: e.target.value })} placeholder="kg CO2e per unit" style={{ width: 120 }} />
          <select value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value })} aria-label="Scope">
            <option value="1">Scope 1</option>
            <option value="2">Scope 2</option>
            <option value="3">Scope 3</option>
          </select>
          <input value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })} placeholder="Source" style={{ width: 220 }} />
          <button onClick={addFactor}>Save factor</button>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>Refrigerant global warming potential (GWP)</div>
        <p className="sub" style={{ marginBottom: 12 }}>
          Tonnes CO2e = charge kg x GWP / 1000, which sets each system's F-gas leak-check interval. A change here moves every affected
          asset's leak-check plan the next time its refrigerant record is saved.
        </p>
        {gwps == null ? <p className="muted">Loading…</p> : null}
        <table className="matrix">
          <thead>
            <tr>
              <th>Refrigerant</th>
              <th>GWP</th>
              <th>F-gas</th>
            </tr>
          </thead>
          <tbody>
            {(gwps ?? []).map((g) => (
              <tr key={g.refrigerant}>
                <td>{g.refrigerant}</td>
                <td>
                  <input
                    defaultValue={String(g.gwp)}
                    style={{ width: 80 }}
                    onBlur={(e) => {
                      const n = Number(e.target.value);
                      if (Number.isInteger(n) && n >= 0 && n !== g.gwp) void saveGwp(g.refrigerant, n, g.is_fgas);
                    }}
                  />
                </td>
                <td>
                  <input type="checkbox" checked={g.is_fgas} onChange={() => void saveGwp(g.refrigerant, g.gwp, !g.is_fgas)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="row" style={{ gap: 8, marginTop: 12, alignItems: 'center' }}>
          <strong style={{ fontSize: 13 }}>Add</strong>
          <input value={gwpDraft.refrigerant} onChange={(e) => setGwpDraft({ ...gwpDraft, refrigerant: e.target.value })} placeholder="e.g. R454B" style={{ width: 100 }} />
          <input value={gwpDraft.gwp} onChange={(e) => setGwpDraft({ ...gwpDraft, gwp: e.target.value })} placeholder="GWP" style={{ width: 70 }} />
          <label style={{ fontSize: 13 }}>
            <input type="checkbox" checked={gwpDraft.isFgas} onChange={() => setGwpDraft({ ...gwpDraft, isFgas: !gwpDraft.isFgas })} /> F-gas
          </label>
          <button
            onClick={() => {
              const n = Number(gwpDraft.gwp);
              if (!gwpDraft.refrigerant.trim() || !Number.isInteger(n) || n < 0) return toast('Enter a refrigerant and a whole-number GWP');
              void saveGwp(gwpDraft.refrigerant.trim(), n, gwpDraft.isFgas);
              setGwpDraft({ refrigerant: '', gwp: '', isFgas: true });
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
